<script>
import {uploadToEdge, getSignNowStatusApi, getCompletionCertificateStatus, saveApplication} from '@/app/api';
import { isArray } from '@incodeapps/js-utilities';
import { mapState, mapActions } from 'vuex';
import ProgramSummaryCard from '@/app/elements/components/ProgramSummaryCard.vue';
import checkMark from "@/assets/img/icons/check-mark.svg";
import exclamationMark from "@/assets/img/icons/exclamation-mark.svg";

export default {
	components: {
		ProgramSummaryCard
	},
	props: {
		application: {
			type: Object,
			default: null
		},
		getApplication: {
			type: Function,
			default: () => {}
		},
		goToNextStep: {
			type: Function,
			default: () => {}
		},
		goToStep: {
			type: Function,
			default: () => {}
		},
		nextStep: {
			type: Boolean,
			default: false
		},
		saveStep: {
			type: Object,
			default: {}
		},
		setNextDisabled: {
			type: Function,
			default: () => {}
		},
		setNextPopoverText: {
			type: Function,
			default: () => {}
		},
	},
	data() {
		return {
			//icons
			checkMark: checkMark,
			exclamationMark: exclamationMark,
			//variables
			isComplete: false,
			isEverythingComplete: false,
			isAllUploaded: true,
			agent: null,
			applicant: {},
			equipments: [],
			coApps: [],
			programType: '',
			app: [],
			note: '',
			requestUploadTypes: {},
			signedEmails: [],
			cocSignedEmails: [],
			confirmAlert: null,
			signStatusInterval: null,
			cocStatusInterval: null,
			uploadTypes: {
				0: {
					name: 'signedContract',
					fileName: null,
					description: "Signed Contract",
					required: false,
					uploaded: false,
					size: 0,
					documents: [],
				},
				1: {
					name: 'billOfSaleInvoice',
					fileName: null,
					description: 'Bill of Sale / Invoice',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				2: {
					name: 'papFormVoidCheque',
					fileName: null,
					description: 'PAP Form / Void Cheque',
					required: true, //this.$route.query.select ? this.$route.query.select : true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				3: {
					name: 'governmentId',
					fileName: null,
					description: 'Government ID',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				4: {
					name: 'governmentId2',
					fileName: null,
					description: '2nd Applicant Government ID',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				5: {
					name: 'completionCertificate',
					fileName: null,
					description: 'Completion Certificate',
					required: true,
					uploaded: false,
					size: 0,
					documents: [],
				},
				6: {
					name: 'incomeVerification',
					fileName: null,
					description: 'Income Verification',
					required: false,
					uploaded: false,
					size: 0,
					documents: [],
				},
				7: {
					name: 'audioFile',
					fileName: null,
					description: 'Audio File',
					required: false,
					uploaded: false,
					size: 0,
					documents: [],
				}
			},
			submitters: {},
			vModels: {
				applicants: [],
				note: '',
				program: {
					financedAmount: 0
				}
			}
		};

	},

	filters: {
		formatMoney(value) {
			return new Intl.NumberFormat('en-US', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(value);
		},
	},

	computed: {

		...mapState({
			authUser: state => state.auth.authUser,
		}),

		dashboardPageTitle() {
			const addr = this.app && this.app.application && this.app.application.address1;
			return addr ? addr : 'Dashboard';
		},

		canEditApplication() {
			if (!this.app || !this.app.application || !this.app.application.status) {
				return false;
			}

			const approvedStatuses = [
				'Approved',
				'Approved Pending Title',
				'Approved Conditional',
				'Approved Pending POI',
				'Ready for Fulfilment',
				'Waiting for Signature',
				'More Audit Info Needed'
			];

			return approvedStatuses.includes(this.app.application.status);
		},
		isLeaseProgram() {
			const code = this.app?.application?.program?.program?.Code;
			return Number(code) === 2;
		},

		completionCertificateMethod() {
			return this.app.application?.docuseal_completion_certificate_method ?? this.app?.application?.completionCertificate?.method;
		},

		/**
		 * Upload rows summarized on the Application Dashboard (mirrors UploadFiles.vue).
		 *
		 * Completion Certificate: show only when `completionCertificate.method === 'print'`
		 * (user chose **GENERATE PDF FOR PRINTING** on CompletionCertificate.vue). Hide when
		 * no method is set yet or when method is **eSignature** (CoC is not a dealer upload).
		 */
		dashboardUploadTypeRows() {
			const method = this.completionCertificateMethod;
			const co = Array.isArray(this.app?.application?.coApps) ? this.app.application.coApps[0] : null;
			const secondApplicantExists = !!(co && (co.firstName || co.lastName || co.name || co.fullName));
			try {
				const co = Array.isArray(this.app.application.coApps) ? this.app.application.coApps[0] : null;
				const secondExists = co && (co.firstName || co.lastName || co.name || co.fullName);
				if (secondExists) {
					const alreadyExists = Object.keys(this.uploadTypes || {}).some(k => this.uploadTypes[k] && this.uploadTypes[k].name === 'governmentId2');
					if (!alreadyExists) {
						this.uploadTypes[4] = {
							name: 'governmentId2',
							fileName: null,
							description: '2nd Applicant Government ID',
							required: true,
							uploaded: false,
							size: 0,
							documents: [],
						};
					}
				}
			} catch (e) {
				console.warn('Could not determine co-applicant existence', e);
			}
			return Object.keys(this.uploadTypes || {})
				.map((k) => ({ idx: k, type: this.uploadTypes[k] }))
				.filter((row) => row.type)
				.filter((row) => {
					let result = true;
					if (row.type.name === "signedContract") {
						result = (['print', 'onlyDealer'].includes(this.app.application.docuseal_submission_method) || (this.app.application?.signNowSentToDealerOnly ?? false));
					} else if (row.type.name === 'governmentId2') {
						result = secondApplicantExists;
					} else if (row.type.name === 'completionCertificate') {
						if (this.authUser.udf_hide_coc) {
							result = false;
						} else {
							result = method === 'print';
						}
					}
					return result;
				})
				.sort((a, b) => Number(a.idx) - Number(b.idx))
				.map((row) => {
					if ((row.type.name === "signedContract" && (['print', 'onlyDealer'].includes(this.app.application.docuseal_submission_method) || this.app.application?.signNowSentToDealerOnly)) ||
						(row.type.name === 'completionCertificate' && method === 'print')) {
						return { idx: row.idx, type: { ...row.type, required: true } };
					}
					return row;
				});
		},
	},
	watch: {
		signedEmails: {
			async handler() {
				await this.refreshSubmitEligibility();
			},
			deep: true,
		},
		cocSignedEmails: {
			async handler() {
				await this.refreshSubmitEligibility();
			},
			deep: true,
		},
		uploadTypes: {
			async handler() {
				await this.refreshSubmitEligibility();
			},
			deep: true,
		},
		'app.application.completionCertificate': {
			async handler() {
				await this.refreshSubmitEligibility();
			},
			deep: true,
		},
		'app.application.signNowSent': {
			async handler() {
				await this.refreshSubmitEligibility();
			},
		},
		'app.application.signNowSentToDealerOnly': {
			async handler() {
				await this.refreshSubmitEligibility();
			},
		},
		"isEverythingComplete": function (val) {
			this.setNextDisabled(!val);
		},
		"nextStep": "sendToEdge"
	},

	async mounted() {
		this.saveLoading(true);
		let app = this.application;
		this.app = app;
		console.log('app', app);
		this.applicant = {
			firstName: app.application.firstName,
			lastName: app.application.lastName,
			middleInitial: app.application.middleInitial,
			email: app.application.email
		}
		if (app.application.hasOwnProperty('agent')) {
			this.agent = app.application.agent
		}
		if (this.app.application.uploadTypes) {
			console.log('application has uploadTypes');
			this.mergeAndDeduplicateUploadTypes(this.app.application.uploadTypes);
		} else {
			console.log('no uploadTypes on application');
		}
		if (
			this.uploadTypes[0]
			&& this.uploadTypes[0].name == 'signedContract'
			&& !(['print', 'onlyDealer'].includes(this.app.application.docuseal_submission_method) || this.app.application.signNowSentToDealerOnly)
		) {

			console.log('not dealer only');
			delete this.uploadTypes[0]
			let temp = this.uploadTypes
			this.uploadTypes = null
			this.uploadTypes = temp
			// this.selectedType = 1

		} else {
			console.log('dealer only');
		}

		console.log('this.uploadTypes[0]', this.uploadTypes[0]);

		if (
			this.uploadTypes[0] == undefined
			// && this.uploadTypes[0].name != 'signedContract'
			&& (['print', 'onlyDealer'].includes(this.app.application.docuseal_submission_method) || this.app.application.signNowSentToDealerOnly)

		) {
			this.uploadTypes[0] = {
				name: 'signedContract',
				fileName: null,
				description: "Signed Contract",
				required: true,
				uploaded: false,
				size: 0,
			};

		}

		console.log('this.applicant', this.applicant);

		if (app.application.equipments) {
			this.equipments = app.application.equipments
		}

		if (app.application.coApps) {
			this.coApps = app.application.coApps
		}

		try {
			if (app.application.docuseal_submission_method === "eSignature" || app.application.signNowSent) {
				await this.checkSignStatus();
				this.startSignStatusChecking();
			}
		} catch (error) {
			console.log('error', error);
		}

		if (app.application.requestUploadTypes) {
			this.requestUploadTypes = app.application.requestUploadTypes
			this.mergeAndDeduplicateUploadTypes(app.application.uploadTypes);

			console.log('app.application.requestUploadTypes', app.application.requestUploadTypes);
			console.log('app.application.uploadTypes', app.application.uploadTypes);
		}

		if (app.application.hasOwnProperty('note')) {
			this.vModels.note = app.application.note
			this.note = app.application.note
		}

		this.vModels.program = app.application.program ?? {};

		if (
			this.vModels.program
			&& this.vModels.program.program
		) {
			this.programType = (this.vModels.program.program.Code == 2) ? 'Lease Payment' : 'Loan Payment'
		} else if (app.application.isImportedFromEdge) {
			this.programType = 'Imported from EDGE, no program details found'
		}

		if (!this.authUser.udf_hide_coc) {
			try {
				const cc = app.application.completionCertificate;
				if (app.application.docuseal_completion_certificate_method === "eSignature" || (cc && cc.method === 'eSignature' && cc.signNowDocId)) {
					await this.checkCoCStatus();
					this.startCoCStatusChecking();
				}
			} catch (e) {
				console.log('completion certificate status', e);
			}
		}

		this.isUploadsDone();
		this.submitters = this.checkSubmitters();
		console.log(this.submitters)

		this.saveLoading(false);

		console.log('await this.checkAppDetails(app.application)', await this.checkApplication(app.application));
		console.log('this.isUploadsDone()', await this.isUploadsDone());
		await this.refreshSubmitEligibility()
		console.log('this.isEverythingComplete', this.isEverythingComplete);

	},

	async beforeDestroy() {
		if (this.confirmAlert) {
			this.removeAlert(this.confirmAlert);
		}
		// Stop checking when component is destroyed
		this.stopSignStatusChecking();
		this.stopCoCStatusChecking();
	},

	beforeRouteLeave(to, from, next) {
		// Stop checking when navigating away from this page
		this.stopSignStatusChecking();
		this.stopCoCStatusChecking();
		next();
	},

	methods: {

		...mapActions([
			'confirm',
			'removeAlerts',
			'saveLoading',
			'showErrorAlert',
			'showInfoAlert',
			'showSuccessAlert',
			'showWarningAlert'
		]),
		checkApplication(data) {
			let result = {
				complete: true,
				errorMessages: []
			};
			const applicantComplete = this.checkApplicant(this.app.application);
			if (!applicantComplete) {
				result.complete = false;
				result.errorMessages.push('Complete applicant information');
			}
			if (isArray(data.coApps)) {
				for (const coApplicant of data.coApps) {
					const coApplicantComplete = this.checkApplicant(coApplicant);
					if (!coApplicantComplete) {
						result.complete = false;
						result.errorMessages.push('Complete co-applicant information');
						break;
					}
				}
			}
			if ((data?.equipments?.length ?? 0) < 1) {
				result.complete = false;
				result.errorMessages.push('Add at least one equipment');
			}
			this.saveLoading(false);
			this.setNextPopoverText(result.errorMessages.join("\n"));
			return result.complete;
		},
		checkApplicant(applicant) {
			let result = true;
			if (!applicant) {
				result = false;
			}
			let required = [
				'address1',
				'city',
				'dob',
				'employer',
				'employmentTerm',
				'employmentTitle',
				'firstName',
				'grossIncome',
				'lastName',
				'postal',
				'primaryPhone',
				'province',
				'titleOwner',
			]
			for (const field of required) {
				if ((applicant?.[field]?.trim?.() || '-') === '-') {
					result = false;
					break;
				}
			}
			return result;
		},
		mergeAndDeduplicateUploadTypes(applicationUploadTypes) {
			const base = JSON.parse(JSON.stringify(this.uploadTypes || {}));
			const incoming = applicationUploadTypes && typeof applicationUploadTypes === 'object'
				? applicationUploadTypes
				: {};
			const canonicalIndexByName = {};
			Object.keys(base).forEach((k) => {
				const slot = base[k];
				if (slot && slot.name) {
					canonicalIndexByName[slot.name] = k;
				}
			});

			Object.keys(incoming).forEach((key) => {
				const incomingSlot = incoming[key];
				const canonicalKey = incomingSlot && incomingSlot.name && canonicalIndexByName[incomingSlot.name] !== undefined
					? canonicalIndexByName[incomingSlot.name]
					: incomingSlot.otherType ? Object.keys(base).length : key;
				base[canonicalKey] = {
					...(base[canonicalKey] || {}),
					...(incomingSlot || {})
				};
			});

			const incomingByName = {};
			Object.keys(incoming).forEach((key) => {
				const slot = incoming[key];
				if (slot && slot.name) {
					incomingByName[slot.name] = canonicalIndexByName[slot.name];
				}
			});

			const deduped = {};
			const seenNames = new Set();
			Object.keys(base)
				.sort((a, b) => Number(a) - Number(b))
				.forEach((key) => {
					const slot = base[key];
					if (!slot) {
						return;
					}
					const slotName = slot.name || `__unnamed_${key}`;
					if (!seenNames.has(slotName)) {
						deduped[key] = slot;
						seenNames.add(slotName);
						return;
					}
					const preferredKey = incomingByName[slotName];
					if (preferredKey !== undefined && String(preferredKey) === String(key)) {
						const existingKey = Object.keys(deduped).find((k) => deduped[k] && deduped[k].name === slotName);
						if (existingKey !== undefined) {
							delete deduped[existingKey];
						}
						deduped[key] = slot;
					}
				});

			this.uploadTypes = deduped;
		},

		async checkSignStatus() {
			try {
				let id = this.$route.params.applicationId;
				let results = await getSignNowStatusApi({ id });
				if (results.docuseal) {//New docuseal logic for checking submitters
					let finished = true;
					let submission = [];
					for (const submitter of results.submitters) {
						if (!this.checkAutoCompleteSubmitter(submitter)) {
							const signed = submitter.status === "completed"
							submission.push({
								id: submitter.id,
								name: submitter.name,
								role: submitter.role,
								signed: signed
							});
							if (!signed) {
								finished = false
							}
						}
					}
					submission.sort(this.sortSubmitters);
					this.submitters.submission = submission;
					if (finished) {
						console.log("All signers complete");
						this.app = await this.getApplication();
						this.stopSignStatusChecking();
					}
					await this.refreshSubmitEligibility();
				} else if (results && Array.isArray(results)) {
					this.signedEmails = [...new Set(
						results
							.map(element => this.normalizeEmail(element))
							.filter(Boolean)
					)];
					console.log("this.signedEmails ==> ", this.signedEmails);
					if (this.areAllRequiredSignersSigned()) {
						console.log('All required signers have signed. Stopping status checks.');
						this.app = await this.getApplication();
						this.stopSignStatusChecking();
					}
					await this.refreshSubmitEligibility();
				}
			} catch (error) {
				console.error('Error checking sign status:', error);
			}
		},

		normalizeEmail(email) {
			if (!email || typeof email !== 'string') return '';
			return email
				.replace(' (dev)', '')
				.replace(' (staging)', '')
				.replace(' (prod)', '')
				.trim()
				.toLowerCase();
		},

		getRequiredSignerEmails() {
			if (!this.app || !this.app.application) {
				return [];
			}

			const signLinks = this.app.application.signLinks;
			const signerEmailsFromLinks = Object.values(signLinks || {})
				.map(link => this.normalizeEmail(link?.email))
				.filter(Boolean);

			if (signerEmailsFromLinks.length > 0) {
				return [...new Set(signerEmailsFromLinks)];
			}

			const fallbackEmails = [];
			if (this.app.application.signNowSentToDealerOnly) {
				fallbackEmails.push(this.normalizeEmail(this.agent?.email));
			} else {
				fallbackEmails.push(this.normalizeEmail(this.applicant?.email));
				(this.coApps || []).forEach((coApp) => {
					fallbackEmails.push(this.normalizeEmail(coApp?.email));
				});
			}

			return [...new Set(fallbackEmails.filter(Boolean))];
		},

		areAllRequiredSignersSigned() {
			const requiredSignerEmails = this.getRequiredSignerEmails();
			if (requiredSignerEmails.length === 0) {
				return false;
			}

			const signedEmailSet = new Set((this.signedEmails || []).map(email => this.normalizeEmail(email)));
			console.log(signedEmailSet, requiredSignerEmails, "Test?");
			return requiredSignerEmails.every(email => signedEmailSet.has(email));
		},

		startSignStatusChecking() {
			// Clear any existing interval
			this.stopSignStatusChecking();

			// Start checking immediately
			this.checkSignStatus();

			// Set up interval to check every 30 seconds
			this.signStatusInterval = setInterval(() => {
				this.checkSignStatus();
			}, 30000); // 30 seconds

			console.log('Started periodic signature status checking');
		},

		stopSignStatusChecking() {
			if (this.signStatusInterval) {
				clearInterval(this.signStatusInterval);
				this.signStatusInterval = null;
				console.log('Stopped periodic signature status checking');
			}
		},

		applicantSignedCoC() {
			const em = this.normalizeEmail(this.applicant?.email);
			return !!(em && (this.cocSignedEmails || []).includes(em));
		},

		async checkCoCStatus() {
			try {
				const id = this.$route.params.applicationId;
				const results = await getCompletionCertificateStatus({ id });
				if (!results || results.errorMessage) {
					return;
				}
				if (results.docuseal) {//New docuseal logic for checking submitters
					console.log("Docuseal workflow initiated for checking status");
					let finished = false;
					for (const submitter of results.submitters) {
						const signed = submitter.status === "completed";
						if (submitter.role === "Applicant") {
							this.submitters.completionCertificate = {
								id: submitter.id,
								name: submitter.name,
								signed: signed
							}
							if (signed) {
								finished = true;
							}
						}
					}
					if (finished) {
						console.log("All signers complete")
						this.app = await this.getApplication();
						this.stopCoCStatusChecking();
					}
					await this.refreshSubmitEligibility();
				} else if (Array.isArray(results)) {
					console.log("SIgn Now emails")
					this.cocSignedEmails = [...new Set(
						results.map((el) => this.normalizeEmail(el)).filter(Boolean)
					)];
					if (this.applicantSignedCoC()) {
						this.app = await this.getApplication();
						this.stopCoCStatusChecking();
					}
					await this.refreshSubmitEligibility();
				}
			} catch (error) {
				console.error('Error checking completion certificate status:', error);
			}
		},

		startCoCStatusChecking() {
			this.stopCoCStatusChecking();
			this.checkCoCStatus();
			this.cocStatusInterval = setInterval(() => {
				this.checkCoCStatus();
			}, 30000);
		},

		stopCoCStatusChecking() {
			if (this.cocStatusInterval) {
				clearInterval(this.cocStatusInterval);
				this.cocStatusInterval = null;
			}
		},

		async handleEditApplication() {
			const message = 'This application has already been fully or provisionally approved for credit. If you continue to edit this application, then you will need to submit the revised application for another credit approval. If you continue to edit this application, any outstanding requests for contract e-signature will be cancelled. Do you want to continue?';

			this.confirmAlert = await this.confirm({
				message: message,
				buttons: [
					{
						text: 'Yes',
						action: async (toast) => {
							if (toast && toast.id) this.$snotify.remove(toast.id);
							try {
								this.saveLoading(true);

								let id = this.$route.params.applicationId;

								// Get the application
								let applicationData = await this.getApplication();
								console.log("applicationData ==> ", applicationData);

								// Set editApplicationStatus to "Edit in Progress"
								let payload = {
									id: id,
									editApplicationStatus: 'Edit in Progress'
								};

								// Save the application with the edit status
								await saveApplication(payload);

								// Determine if it's a lease program
								const isLease = applicationData.application?.program?.programType === 'Lease' ||
									applicationData.application?.program?.leaseTermMonths != null;

								// Navigate to the appropriate calculator
								const calculatorName = isLease ? 'leaseCalculator' : 'loanCalculator';
								await this.$router.push({ name: calculatorName, params: { applicationId: id } });
								this.saveLoading(false);
							} catch (err) {
								console.error(err);
								this.saveLoading(false);
								this.showErrorAlert('Error editing application: ' + (err.message || 'Unknown error'));
							}
						},
						bold: false
					},
					{
						text: 'No',
						action: (toast) => {
							if (toast && toast.id) this.$snotify.remove(toast.id);
						}
					}
				]
			});

			if (this.confirmAlert) {
				this.confirmAlert.on('hidden', () => {
					this.confirmAlert = null;
				});
			}
		},

		async sendToEdge() {
			if (this.nextStep) {
				let isErrorShown = false

				this.saveLoading(true);

				let result = await uploadToEdge({
					id: this.app.application.id
				}).catch(error => {

					isErrorShown = true

					console.log('error', error);

					if (error.response.data && error.response.data.errorMessage) {

						this.showErrorAlert(error.response.data.errorMessage);

					} else {
						this.showErrorAlert(error.message);
					}

				});

				console.log('result', result);
				console.log('result type:', typeof result);
				console.log('result keys:', result ? Object.keys(result) : 'null');

				// Handle the new backend response format
				if (result && result.success === true) {

					this.showSuccessAlert('All files have been uploaded successfully')

					return this.$router.push({
						name: `Dashboard`,
					});

				} else if (result && (result.success === false || result.failedUploads)) {

					// Handle partial or complete failure
					if (!isErrorShown) {
						console.log('Handling partial/full failure with result:', result);
						let errorMessage = result.uploadStatus || result.errorMessage || 'Upload failed';

						// Add details about failed files if available
						if (result.failedUploads && result.failedUploads.length > 0) {
							errorMessage += '\n\n❌ Failed Files:';

							// Add specific error messages for each failed file
							result.failedUploads.forEach(file => {

								if (
									file
									&& file != 'null'
									&& file.error
									&& file.error != 'undefined'
									&& file.error != 'null'
								) {
									errorMessage += `\n• ${file.filename}: ${file.error}`;
								} else {
									errorMessage += `\n• ${file.filename}: Unknown error`;
								}
							});

							// Add successful files if any
							// if (result.successfulUploads && result.successfulUploads.length > 0) {
							// 	errorMessage += '\n\n✅ Successfully Uploaded:';
							// 	result.successfulUploads.forEach(file => {
							// 		errorMessage += `\n• ${file.filename}`;
							// 	});
							// }
						}

						console.log('Final error message:', errorMessage);
						this.showErrorAlert(errorMessage);
						if (this.saveStep.onError) {
							this.saveStep.onError();
						}
					}

				} else {
					// Fallback for old response format or unexpected responses
					if (!isErrorShown) {
						console.log('Fallback error handling - result:', result);
						console.log('Result type:', typeof result);
						console.log('Result keys:', result ? Object.keys(result) : 'null');
						console.log('Result success:', result ? result.success : 'undefined');
						console.log('Result failedUploads:', result ? result.failedUploads : 'undefined');

						if (result && result.errorMessage) {
							this.showErrorAlert('Something went wrong. ' + result.errorMessage);
						} else if (result && result.uploadStatus) {
							this.showErrorAlert('Something went wrong. ' + result.uploadStatus);
						} else {
							this.showErrorAlert('Something went wrong. ' + JSON.stringify(result, null, 2));
						}
					}
				}
			}
			this.saveLoading(false);
		},

		async saveNote() {
			this.saveLoading(true);
			 await saveApplication({
					id: this.app.application.id,
					note: this.vModels.note
			});
			this.app.application.note = this.vModels.note
			this.note = this.vModels.note
			this.saveLoading(false);
		},

		async saveEmail(index) {
			console.log('saveEmail', index);
			this.saveLoading(true);
			if (index == 'main') {
				this.app.application.email = this.applicant.email
				await saveApplication({
						id: this.app.application.id,
						email: this.applicant.email
				});
			}
			if (index != 'main') {
				if (
					this.app.application.coApps
					&& isArray(this.app.application.coApps)
				) {
					this.app.application.coApps[index].email = this.coApps[index].email
					await saveApplication({
						...this.app.application
					});
				}
			}
			location.reload();//TODO: change this to getApplication
			this.saveLoading(false);
		},

		async resetNote() {

			this.vModels.note = this.app.application.note;
			this.note = this.app.application.note;

		},

		isContractExecutionComplete() {
			const a = this.app?.application;
			console.log(a, "A?")
			if (!a) {
				return false;
			}
			if (a.signNowSentToDealerOnly) {
				return true;
			}
			if (a.signNowSent) {
				if (!a.signNowSentToDealerOnly)
				{
					return this.areAllRequiredSignersSigned();
				}
			} else {
				let result = true;
				if (a?.docuseal_submission_method === "eSignature") {
					for (const submitter of this.submitters.submission ?? []) {
						if (!submitter.signed) {
							result = false;
							break;
						}
					}
				}
				return result;
			}
			return false;
		},

		/**
		 * Completion Certificate is a separate step (CompletionCertificate.vue).
		 * - No `application.completionCertificate.method` yet → not complete (do not treat like eSignature or print).
		 * - eSignature → applicant must appear in CoC SignNow status (applicantSignedCoC).
		 * - print → scanned file is required; that row appears in dashboardUploadTypeRows and is enforced by isUploadsDone.
		 */
		isCompletionCertificateFlowComplete() {
			const method = this.app.application?.docuseal_completion_certificate_method ?? this.app?.application?.completionCertificate?.method ?? null;
			if (!method) {
				return false;
			}
			if (method === 'eSignature') {
				return this.submitters.completionCertificate?.signed ?? this.applicantSignedCoC();
			}
			if (method === 'print') {
				return true;
			}
			return false;
		},

		async refreshSubmitEligibility() {
			const application = this.app?.application;
			if (!application) {
				this.isEverythingComplete = false;
				return false;
			}

			const appDetailsComplete = (await this.checkApplication(application)) === true;
			const uploadsComplete = await this.isUploadsDone();
			const completionCertificateComplete = this.isCompletionCertificateFlowComplete();
			const contractEsignComplete = this.isContractExecutionComplete();
			this.isEverythingComplete = appDetailsComplete
				&& uploadsComplete
				&& completionCertificateComplete
				&& contractEsignComplete;
			return this.isEverythingComplete;
		},

		async isUploadsDone() {

			console.log('isUploadsDone()');
			console.log('this.uploadTypes', this.uploadTypes);

			let isAllUploaded = true;

			this.dashboardUploadTypeRows.forEach((row) => {
				const t = row.type;
				console.log(t.name, ': \nrequested: ', t.isRequested, ', \nuploaded: ', t.uploaded);

				if (
					(!t.uploaded || t.uploaded === undefined)
					&& t.required
				) {
					console.log('isAllUploaded false for row: ', row.idx, t);
					isAllUploaded = false;
				}

			});

			this.isAllUploaded = isAllUploaded;

			console.log('isAllUploaded', isAllUploaded);
			return isAllUploaded;
		},
		sortSubmitters(a,b) {
			const sortOrder = {
				"Dealer": 0,
				"Applicant": 1,
				"Co-applicant 1": 2,
				"Co-applicant 2": 3,
				"Co-applicant 3": 4,
			}
			return sortOrder[a.role] - sortOrder[b.role];
		},
		checkAutoCompleteSubmitter(submitter) {
			return submitter.submission_events.find(item => item.event_type === "api_complete_form")
		},
		checkSubmitters() {//New function for applications using docuseal
			const result = {};
			const { docuseal_submitters, docuseal_completion_certificate_submitters } = this.app?.application ?? {};
			if (docuseal_submitters) {
				result.submission = [];
				for (const submitter of docuseal_submitters) {
					if (!this.checkAutoCompleteSubmitter(submitter)) {
						result.submission.push({
							id: submitter.id,
							name: submitter.name,
							role: submitter.role,
							signed: submitter.status === "completed",
						});
					}
					result.submission.sort(this.sortSubmitters);
				}
			}
			if (docuseal_completion_certificate_submitters) {
				const foundSigner = docuseal_completion_certificate_submitters.find(submitter => submitter.role === "Applicant");
				if (foundSigner) {
					result.completionCertificate = {
						id: foundSigner.id,
						name: foundSigner.name,
						signed: foundSigner.status === "completed",
					};
				}
			}
			return result;
		}
	}
};
</script>


<template>
	<div class="application-dashboard-template">
		<div class="container-xl">
			<div class="row">
				<div class="col-18">
					<div class="template__page-title">
						<h1>{{ dashboardPageTitle }}</h1>
					</div>
				</div>
				<div class="col-6 d-flex justify-content-end align-items-center">
					<button v-if="canEditApplication" type="button" class="tffi-btn tffi-btn--solid theming accent-color fw-bold"
						@click="handleEditApplication">
						Edit Application
					</button>
				</div>
			</div>
		</div>

		<form @submit.prevent="() => {}" novalidate>
			<div class="container-xl template--vh-">
				<div class="row">
					<!-- Summary Card - Left Side -->
					<div class="col-lg-8">
						<ProgramSummaryCard
							v-if="!vModels.program.leaseTermMonths"
							:program="vModels.program"
							:program-type="programType"
							title="Installment loan estimate"
							:clickable="false"
						/>
						<div v-if="!vModels.program.leaseTermMonths && authUser.usr_ShowDealerCosts"
							class="tffi-card tffi-card-summary-card">
							<div class="tffi-card-summary-card__bottom-area">
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Cost Rate: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">{{ vModels.program.dealerCostRate | formatMoney }}%</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Cost: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.program.dealerCost | formatMoney }}</p>
								</div>
								<div class="tffi-card-summary-card__bottom-area__label">
									<p class="theming primary-color fw-bold mb-0">Dealer Admin Fee: </p>
									<span class="flex-grow-1 d-none d-lg-flex"></span>
									<p class="theming primary-color fw-bold mb-0">${{ vModels.program.dealerAdminFee | formatMoney }}</p>
								</div>
							</div>
						</div>
						<!--Lease -->
						<ProgramSummaryCard
							v-if="vModels.program.leaseTermMonths"
							:program="vModels.program"
							:program-type="programType"
							:clickable="false"
						/>
						<button v-if="!note" type="button" class="tffi-btn tffi-btn--solid theming accent-color fw-bold w-100 mb-3"
							data-bs-toggle="modal" data-bs-target="#notesModal">
							Add Note
						</button>
					</div>
					<!-- Right Side - Stacked vertically -->
					<div class="col-lg-16">
						<!-- Applicant Details -->
						<div class="row mb-3">
							<div class="col-12">
								<div style="cursor: pointer;" data-bs-backdrop="static" data-bs-keyboard="false" data-bs-toggle="modal"
									data-bs-target="#notesModalMainApp" v-if="applicant && applicant.firstName"
									class="tffi-card tffi-card-secondary-card theming primary-color person">
									<div class="d-none- tffi-card-secondary-card__left-area">
										<div class="tffi-card-secondary-card__left-area__status-indicator">
											<div v-if="checkApplicant(app.application)" class="status-accepted">
												<img :src="checkMark" alt="">
											</div>
											<div v-if="!checkApplicant(app.application)" class="status-denied">
												<img :src="exclamationMark" alt="">
											</div>
										</div>
									</div>
									<div class="tffi-card-secondary-card__right-area">
										<div class="tffi-card-secondary-card__right-area__title-area">
											<h2>Applicant</h2>
										</div>
										<div class="tffi-card-secondary-card__right-area__content-area">
											<p>{{ applicant['firstName'] + ' ' + (applicant['middleInitial'] ?? '') + ' ' +
												applicant['lastName']
												}}</p>
											<p>{{ applicant['email'] }}</p>
										</div>
									</div>
								</div>

								<!-- Co-Applicants -->
								<div v-if="coApps" v-for="(coApp, index) in coApps" class="row mb-3">
									<div class="col-24">
										<div style="cursor: pointer;" data-bs-backdrop="static" data-bs-keyboard="false"
											data-bs-toggle="modal" :data-bs-target="'#notesModal' + index"
											class="tffi-card tffi-card-secondary-card person">
											<div class="d-none- tffi-card-secondary-card__left-area">
												<div class="tffi-card-secondary-card__left-area__status-indicator">
													<div v-if="checkApplicant(coApps[index])" class="status-accepted">
														<img :src="checkMark" alt="">
													</div>
													<div v-if="!checkApplicant(coApps[index])" class="status-denied">
														<img :src="exclamationMark" alt="">
													</div>
												</div>
											</div>
											<div class="tffi-card-secondary-card__right-area">
												<div class="tffi-card-secondary-card__right-area__title-area">
													<h2>Co-Applicant</h2>
												</div>
												<div class="tffi-card-secondary-card__right-area__content-area">
													<p>{{ coApp['firstName'] + ' ' + (coApp['middleInitial'] ?? '') + ' ' + coApp['lastName'] }}
													</p>
													<p>{{ coApp['email'] }}</p>
												</div>
											</div>
										</div>
									</div>
								</div>

							</div>
							<div class="col-12">
								<div v-if="equipments" v-for="(equipment, index) in equipments"
									class="tffi-card tffi-card-secondary-card">
									<div class="tffi-card-secondary-card__left-area">
									</div>
									<div class="tffi-card-secondary-card__right-area">
										<div class="tffi-card-secondary-card__right-area__title-area">
											<h2>Equipment</h2>
										</div>
										<div class="tffi-card-secondary-card__right-area__content-area">
											<p>Type: {{ equipment['equipmentType'] }}</p>
											<p>Brand: {{ equipment['brand'] }}</p>
											<p>Model: {{ equipment['model'] }}</p>
										</div>
									</div>
								</div>
							</div>
						</div>
						<!-- Notes Section -->
						<div class="row mb-3">
							<div class="col-24">
								<div @click="resetNote" data-bs-backdrop="static" data-bs-keyboard="false" data-bs-toggle="modal"
									data-bs-target="#notesModal" v-if="note" class="tffi-card tffi-card-summary-card sticky-note theming">
									<p class="strong">Notes</p>
									{{ note }}
								</div>
							</div>
						</div>
						<!-- Gold Line -->
						<div class="row mb-3">
							<div class="col-24">
								<hr class="theming primary-color my-5">
							</div>
						</div>
						<!-- E-Signatures + Completion Certificate | Upload Files -->
						<div class="row mb-3">
							<div class="col-24 col-lg-12 mb-3 mb-lg-0">
								<div class="d-flex flex-column gap-2">
									<!-- E-Signatures -->
									<div @click="goToStep({ name: 'sign' })"
										class="tffi-card tffi-card-status-list-card cursor-pointer">
										<div class="tffi-card-status-list-card__left-area">
											<div class="tffi-card-status-list-card__left-area__status-indicator">
												<div class="d-none status-accepted">
													<img :src="checkMark" alt="">
												</div>
											</div>
										</div>
										<div class="tffi-card-status-list-card__right-area">
											<div class="tffi-card-status-list-card__right-area__title-area">
												<h2>E-Signatures</h2>
											</div>
											<div class="tffi-card-status-list-card__right-area__content-area">
												<ul class="tffi-card-status-list-card__list list-unstyled">
													<li v-if="app && app.application && !app.application?.signNowSent && !app.application.docuseal_submission_method"
														class="tffi-card-status-list-card__list__item">
														Click here to generate contract for signature
													</li>
													<li
														v-if="app && app.application && (app.application.docuseal_submission_id ?? app.application?.signNowSent) && (['print', 'onlyDealer'].includes(app.application.docuseal_submission_method) || app.application?.signNowSentToDealerOnly)"
														class="tffi-card-status-list-card__list__item">
														You have chosen to upload a scanned signed contract
													</li>
													<li
														v-if="app && app.application && app.application?.signNowSent && !app.application?.signNowSentToDealerOnly && agent && !isLeaseProgram"
														class="tffi-card-status-list-card__list__item">
														<span v-if="app && app.application && agent"
															class="tffi-card-status-list-card__list__item__name">
															{{ agent['fullName'] }}
														</span>
														<span class="flex-grow-1"></span>
														<span class="tffi-card-status-list-card__list__item__status">
															<img v-if="signedEmails.length > 0 && signedEmails.includes(agent.email.toLowerCase())"
																class="" :src="checkMark" alt="">
															<img v-else :src="exclamationMark" alt="">
														</span>
													</li>

													<li
														v-if="app && app.application && app.application?.signNowSent && !app.application?.signNowSentToDealerOnly"
														class="tffi-card-status-list-card__list__item">
														<span v-if="app && app.application && applicant"
															class="tffi-card-status-list-card__list__item__name">
															{{ applicant['firstName'] + ' ' + (applicant['middleInitial'] ?? '') + ' ' +
															applicant['lastName'] }}
														</span>
														<span class="flex-grow-1"></span>
														<span class="tffi-card-status-list-card__list__item__status">
															<img
																v-if="(applicant?.email && signedEmails.length > 0 && signedEmails.includes(applicant.email.toLowerCase())) || (app.application?.signLinks && app.application?.signLinks.length > 0 && signedEmails.includes(app.application?.signLinks[1]?.email.toLowerCase()))"
																class="" :src="checkMark" alt="">
															<img v-else :src="exclamationMark" alt="">
														</span>
													</li>

													<li
														v-if="app && app.application && !app.application?.signNowSent && ['eSignature', 'embedded'].includes(app.application.docuseal_submission_method)"
														v-for="submitter in submitters.submission ?? []" :key="submitter.id"
														class="tffi-card-status-list-card__list__item">
														<span class="tffi-card-status-list-card__list__item__name">
															{{ submitter.name ?? applicant['firstName'] + ' ' + (applicant['middleInitial'] ?? '') + ' ' +
														applicant['lastName'] }}
														</span>
														<span class="flex-grow-1"></span>
														<span class="tffi-card-status-list-card__list__item__status">
															<img
																v-if="submitter.signed"
																class="" :src="checkMark" alt="">
															<img v-else :src="exclamationMark" alt="">
														</span>
													</li>

													<li
														v-if="app && app.application && app.application?.signNowSent && !app.application?.signNowSentToDealerOnly && coApps"
														v-for="(coApp, index) in this.coApps" class="tffi-card-status-list-card__list__item">
														<span class="tffi-card-status-list-card__list__item__name">
															{{ coApp['firstName'] + ' ' + (coApp['middleInitial'] ?? '') + ' ' + coApp['lastName'] }}
														</span>
														<span class="flex-grow-1"></span>
														<span class="tffi-card-status-list-card__list__item__status">
															<img
																v-if="(coApp.email && signedEmails.length > 0 && signedEmails.includes(coApp?.email.toLowerCase()) || (app.application?.signLinks && signedEmails.includes(app.application?.signLinks[index + 2]?.email)) )"
																class="" :src="checkMark" alt="">
															<img v-else :src="exclamationMark" alt="">
														</span>
													</li>

												</ul>
											</div>
										</div>
									</div>
									<!-- Completion Certificate -->
									<div @click="goToStep({ name: 'completion' })"
										class="tffi-card tffi-card-status-list-card cursor-pointer" v-if="!authUser.udf_hide_coc">
										<div class="tffi-card-status-list-card__left-area">
											<div class="tffi-card-status-list-card__left-area__status-indicator">
												<div class="d-none status-accepted">
													<img :src="checkMark" alt="">
												</div>
											</div>
										</div>
										<div class="tffi-card-status-list-card__right-area">
											<div class="tffi-card-status-list-card__right-area__title-area">
												<h2>Completion Certificate</h2>
											</div>
											<div class="tffi-card-status-list-card__right-area__content-area">
												<ul class="tffi-card-status-list-card__list list-unstyled">
													<li v-if="!completionCertificateMethod"
														class="tffi-card-status-list-card__list__item">
														Click here to generate completion certificate.
													</li>
													<li v-if="completionCertificateMethod === 'print'"
														class="tffi-card-status-list-card__list__item">
														You have chosen to upload a scanned signed Completion Certificate.
													</li>
													<li v-if="completionCertificateMethod === 'eSignature'"
														class="tffi-card-status-list-card__list__item">
														<span v-if="applicant"
															class="tffi-card-status-list-card__list__item__name">
															{{ applicant['firstName'] + ' ' + (applicant['middleInitial'] ?? '') + ' ' +
															applicant['lastName'] }}
														</span>
														<span class="flex-grow-1"></span>
														<span class="tffi-card-status-list-card__list__item__status">
															<img v-if="submitters?.completionCertificate?.signed ?? applicantSignedCoC()" :src="checkMark" alt="">
															<img v-else :src="exclamationMark" alt="">
														</span>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div class="col-24 col-lg-12">
									<!-- Upload Files -->
									<div @click="goToStep({ name: 'documents' })"
										class="tffi-card tffi-card-status-list-card upload-files-card cursor-pointer">
										<div class="tffi-card-status-list-card__left-area">
											<div class="tffi-card-status-list-card__left-area__status-indicator">
												<div class="status-accepted">
													<img v-if="isAllUploaded" :src="checkMark" alt="">
													<img v-if="!isAllUploaded" :src="exclamationMark" alt="">
												</div>
											</div>
										</div>
										<div class="tffi-card-status-list-card__right-area">
											<div class="tffi-card-status-list-card__right-area__title-area">
												<h2>Upload Files</h2>
											</div>
											<div class="tffi-card-status-list-card__right-area__content-area">
												<ul class="tffi-card-status-list-card__list list-unstyled">

													<li v-for="row in dashboardUploadTypeRows" :key="row.idx"
														class="tffi-card-status-list-card__list__item">
														<span class="tffi-card-status-list-card__list__item__name">
															{{ row.type.description }}
														</span>
														<span class="flex-grow-1"></span>
														<span class="tffi-card-status-list-card__list__item__status">
															<img v-if="row.type.uploaded" :src="checkMark" alt="">
															<img v-if="row.type.required && !row.type.uploaded" :src="exclamationMark" alt="">
														</span>
													</li>
												</ul>
											</div>
										</div>
									</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Modals -->
			<!-- Main Applicant Modal -->
			<div class="modal fade show-" id="notesModalMainApp" tabindex="-1" aria-labelledby="notesModalMainAppLabel"
				aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h3 class="modal-title theming primary-color">Change email</h3>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body" @click.stop>
							<p>
								You may edit the applicant's email address. If a contract has already been generated, then editing
								the email address will cause e-signature invitations in progress to be cancelled. You will need to
								return to the e-signature page to generate a new contract and initiate new e-signature
								invitations. You may cancel if you prefer not to edit the email address.
							</p>
							<form-field v-model="applicant.email" name="note" bootstrap type="text" @click.stop></form-field>
						</div>
						<div class="modal-footer">
							<button @click="saveEmail('main')" data-bs-dismiss="modal" type="button"
								class="tffi-btn tffi-btn--solid theming primary-color w-100 fw-bold">Save email</button>
							<button data-bs-dismiss="modal" type="button"
								class="tffi-btn tffi-btn--solid theming accent-color w-100 fw-bold">Cancel</button>
						</div>
					</div>
				</div>
			</div>
			<!-- Co-Applicant Modals -->
			<div v-if="coApps" v-for="(coApp, index) in coApps" class="modal fade show-" :id="'notesModal' + index"
				tabindex="-1" :aria-labelledby="'notesModal' + index + 'Label'" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h3 class="modal-title theming primary-color">Change email</h3>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body" @click.stop>
							<p>
								You may edit the applicant's email address. If a contract has already been generated, then editing
								the email address will cause e-signature invitations in progress to be cancelled. You will need to
								return to the e-signature page to generate a new contract and initiate new e-signature
								invitations. You may cancel if you prefer not to edit the email address.
							</p>
							<form-field v-model="coApp.email" name="note" bootstrap type="text" @click.stop></form-field>
						</div>
						<div class="modal-footer">
							<button @click="saveEmail(index)" data-bs-dismiss="modal" type="button"
								class="tffi-btn tffi-btn--solid theming primary-color w-100 fw-bold">Save email</button>
							<button data-bs-dismiss="modal" type="button"
								class="tffi-btn tffi-btn--solid theming accent-color w-100 fw-bold">Cancel</button>
						</div>
					</div>
				</div>
			</div>
			<!-- Notes Modal -->
			<div class="modal fade show-" id="notesModal" tabindex="-1" aria-labelledby="notesModalLabel" aria-hidden="true">
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-body">
							<form-field v-model="vModels.note" name="note" bootstrap multiline type="textarea" rows="5"></form-field>
						</div>
						<div class="modal-footer d-flex justify-content-between" style="flex-direction: row !important;">
							<button data-bs-dismiss="modal" type="button"
								class="tffi-btn tffi-btn--solid theming accent-color fw-bold"
								style="flex: 0 0 auto; width: auto;">Cancel</button>
							<button @click="saveNote" data-bs-dismiss="modal" type="button"
								class="tffi-btn tffi-btn--solid theming primary-color fw-bold"
								style="flex: 0 0 auto; width: auto;">Save Note</button>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
</template>

<style lang="scss" scoped>
.edit-btn {
	position: relative;
	top: -1rem;
	right: -1rem
}

.card-body {

	.form-group.static-form-group {

		&:last-child {
			border-bottom: none;
			margin-bottom: 0;
		}

	}

	.invalid-feedback {
		margin-top: -1rem;
		margin-bottom: 1rem;
	}

}

.form-control-plaintext {
	white-space: pre-line;
}

.tffi-card-secondary-card.person,
.tffi-card-secondary-card.person {
	&:hover {
		border: 2px solid #C18F14;
	}
}

.sticky-note {

	padding: 40px;
	padding-top: 20px;
	overflow-wrap: anywhere;
	cursor: pointer;
	background-color: #DFCE86;
	border: 2px solid transparent;

	&:hover {
		border: 2px solid #C18F14;
	}

}

.cursor-pointer {
	cursor: pointer;
	&:hover {
		border: 2px solid #C18F14;
	}
}

</style>
